import React, { useState, useEffect, useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faSpinner } from '@fortawesome/free-solid-svg-icons';
import { InsightsService, sharedApiClient } from '../../fetcher';
import { UserActionVerbs } from '@medprompt/shared';

// Entity types
const EntityType = {
  DOCUMENT: 'DOCUMENT',
  CONSULTATION: 'CONSULTATION',
  TASK: 'TASK',
  REFERRAL: 'REFERRAL'
};

// Helper function to format user action events
const formatEventDescription = (event, activeUsers = [], entityType = EntityType.DOCUMENT, teams = [], assigneeHistory = []) => {
  if (!event.userEvent) return '';
  
  const { userEvent, userAction } = event;
  
  // Find the actual user name from activeUsers, fallback to userId substring if not found
  let user = 'Unknown';
  if (userEvent.userId) {
    if (userEvent.userId === 'SYSTEM') {
      user = 'MedPrompt App'
    } else {
      const foundUser = activeUsers.find(u => u.userId === userEvent.userId);
      user = foundUser ? foundUser.name : userEvent.userId.substring(0, 5);
    }
  }

  const action = UserActionVerbs[userAction] || userAction.toLowerCase();
  
  // Determine context based on statusType or entity type
  let context = entityType === EntityType.TASK ? 'task' 
    : entityType === EntityType.REFERRAL ? 'referral' 
    : entityType === EntityType.CONSULTATION ? 'consultation' 
    : 'document';
  if (userEvent.statusType) {
    const statusTypeMap = {
      SUMMARY: 'summary',
      BRIEF_SUMMARY: 'brief summary',
      NHS_ID: 'NHS ID',
      OWNING_PATIENT: 'owning patient',
      OUTCOME: 'outcome',
      EXISTING_CODES: 'existing codes',
      NEW_CODES: 'new codes',
      POTENTIAL_EXISTING_CODES: 'potential existing codes',
      DETAILS: 'details',
      COMMENT: 'comment',
      ASSIGNEE: 'assignee',
      DOCUMENT_ROTATION: 'document rotation',
      MEASUREMENTS: 'measurements',
      RISK_CATEGORY: 'urgency',
      LMC_CONTRACT_VIOLATION: 'LMC contract violation',
      TITLE: 'title',
      LETTER_TYPE: 'letter type',
      ALL: 'all content',
      // Referral status types
      TEXT: 'referral notes',
      REFERRAL_TYPE: 'referral type',
      REFERRED_AT: 'time of referral',
      COMPLETE: 'completion status',
      VIEWER: 'viewer',
      // Consultation status types
      CONSULTATION_SUMMARY: 'consultation summary',
      CONSULTATION_NOTES: 'consultation notes',
      CONSULTATION_CODES: 'consultation codes',
      CONSULTATION_MEASUREMENTS: 'consultation measurements',
      CONSULTATION_TASKS: 'consultation tasks'
    };
    context = statusTypeMap[userEvent.statusType] || userEvent.statusType.toLowerCase();
    
    // Enhanced ASSIGNEE context with assignee history
    if (userEvent.statusType === 'ASSIGNEE' && assigneeHistory && assigneeHistory.length > 0) {
      // Find assignee history entry with exact timestamp match
      const historyEntry = assigneeHistory.find(entry => 
        entry.timestamp === userEvent.timestamp
      );
      
      if (historyEntry) {
        // Resolve assignee name from assigneeId (could be userId or teamId)
        let assigneeName = 'Unknown';
        if (historyEntry.assigneeId) {
          // First check users
          const foundUser = activeUsers.find(u => u.userId === historyEntry.assigneeId);
          if (foundUser) {
            assigneeName = foundUser.name;
          } else {
            // Then check teams
            const foundTeam = teams.find(t => t.teamId === historyEntry.assigneeId);
            if (foundTeam) {
              assigneeName = foundTeam.teamName;
            } else {
              // Fallback to substring if not found
              assigneeName = historyEntry.assigneeId.substring(0, 5);
            }
          }
        }
        
        context = `assignee to ${assigneeName}`;
      }
    }
  }
  
  return `${user} ${action} ${context}`;
};

// Helper function to format timestamp
const formatTimestamp = (timestamp) => {
  const date = new Date(timestamp);
  return date.toLocaleString();
};

// Helper function to format time difference between two timestamps
const formatTimeDifference = (laterTimestamp, earlierTimestamp) => {
  const diffMs = new Date(laterTimestamp) - new Date(earlierTimestamp);
  const diffSeconds = Math.floor(diffMs / 1000);
  
  if (diffSeconds < 0) return '';
  
  const days = Math.floor(diffSeconds / (24 * 3600));
  const hours = Math.floor((diffSeconds % (24 * 3600)) / 3600);
  const minutes = Math.floor((diffSeconds % 3600) / 60);
  const seconds = diffSeconds % 60;
  
  const parts = [];
  
  if (days > 0) parts.push(`${days}d`);
  if (hours > 0) parts.push(`${hours}h`);
  if (minutes > 0) parts.push(`${minutes}m`);
  if (seconds > 0 || parts.length === 0) parts.push(`${seconds}s`);
  
  // Return up to 2 significant parts
  return parts.slice(0, 2).join(' ');
};

const ActivityModal = ({ 
  // New props for generic entity support
  entityType = EntityType.DOCUMENT,
  entityId,
  entity,
  // Legacy props for backward compatibility
  doc,
  documentId,
  // Common props
  activeUsers, 
  teams = [],
  onClose 
}) => {
  const [auditData, setAuditData] = useState(null);
  const [documentAuditData, setDocumentAuditData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Resolve entity type and ID from either new or legacy props
  const resolvedEntityType = entityType;
  const resolvedEntityId = entityId || documentId;
  const resolvedEntity = entity || doc;
  
  // Get available actions based on entity type
  const getAvailableActions = () => {
    if (resolvedEntityType === EntityType.TASK) {
      // mirrors taskActions() = [OPEN, EDIT, COMPLETE, DELETE]
      return {
        OPEN: false,
        EDIT: true,
        COMPLETE: true,
        DELETE: true
      };
    }
    if (resolvedEntityType === EntityType.REFERRAL) {
      // mirrors referralActions() = [CREATE, OPEN, EDIT, COMPLETE, DELETE]
      return {
        CREATE: true,
        OPEN: false,
        EDIT: true,
        COMPLETE: true,
        DELETE: true
      };
    }
    if (resolvedEntityType === EntityType.CONSULTATION) {
      // mirrors consultationActions() = [CREATE, OPEN, EDIT, COMPLETE_RECORDING, REGENERATE, DELETE]
      return {
        OPEN: false,
        EDIT: true,
        COMPLETE_RECORDING: true,
        REGENERATE: true,
        DELETE: true
      };
    }
    return {
      OPEN: false,
      EDIT: true,
      REGENERATE: true,
      COMMUNICATE: true,
      FILE_AWAY: true,
      DELETE: true
    };
  };
  
  // Filter state - dynamically set based on entity type
  const [actionFilters, setActionFilters] = useState(getAvailableActions());
  
  const insightsManager = useMemo(() => new InsightsService(sharedApiClient), []);

  useEffect(() => {
    const fetchAuditData = async () => {
      try {
        if (resolvedEntityType === EntityType.DOCUMENT) {
          const data = await insightsManager.fetchDocumentAudit(resolvedEntityId);
          setAuditData(data);
        } else if (resolvedEntityType === EntityType.CONSULTATION) {
          const consultationAuditData = await insightsManager.fetchConsultationAudit(resolvedEntityId);
          setAuditData(consultationAuditData);
        } else if (resolvedEntityType === EntityType.TASK) {
          // Fetch task audit
          const taskAuditData = await insightsManager.fetchTaskAudit(resolvedEntityId);
          setAuditData(taskAuditData);
          
          // Fetch document audit if documentId exists (to get FILE_AWAY event for attribution)
          if (resolvedEntity?.documentId) {
            try {
              const docAuditData = await insightsManager.fetchDocumentAudit(resolvedEntity.documentId);
              setDocumentAuditData(docAuditData);
            } catch (docError) {
              console.warn('Failed to fetch document audit for task attribution:', docError);
              // Continue without document audit data
            }
          }
        } else if (resolvedEntityType === EntityType.REFERRAL) {
          const referralAuditData = await insightsManager.fetchReferralAudit(resolvedEntityId);
          setAuditData(referralAuditData);
        }
        setLoading(false);
      } catch (error) {
        setError(error);
        setLoading(false);
        console.error('Failed to fetch audit data:', error);
      }
    };

    fetchAuditData();
  }, [resolvedEntityId, resolvedEntityType]);

  // Handle filter toggle
  const handleFilterToggle = (action) => {
    setActionFilters(prev => ({
      ...prev,
      [action]: !prev[action]
    }));
  };

  // Create timeline events from audit data
  const getTimelineEvents = () => {
    if (!auditData) return [];
    
    const events = [];
    
    if (resolvedEntityType === EntityType.DOCUMENT) {
      // Add system event for document received/uploaded (first chronologically)
      // Check if there's a CREATE user action to determine uploader
      let createEvent = null;
      if (auditData.userEvents) {
        createEvent = auditData.userEvents.find(event => event.userAction === 'CREATE');
      }
      
      if (createEvent) {
        // CREATE action exists - determine if system or user upload
        const userId = createEvent.userEvent.userId;
        let description = 'MedPrompt received document'; // default for SYSTEM
        
        if (userId !== 'SYSTEM') {
          // User uploaded the document
          const foundUser = activeUsers.find(u => u.userId === userId);
          const userName = foundUser ? foundUser.name : userId.substring(0, 5);
          description = resolvedEntity?.senderEmail
            ? `${userName} uploaded document via email`
            : `${userName} uploaded document manually`;
        } else if (resolvedEntity?.senderMeshMailboxId) {
          // System received via MESH
          description = 'MedPrompt received document via MESH';
        }
        
        events.push({
          timestamp: createEvent.userEvent.timestamp,
          description: description,
          isSystem: true
        });
      } else if (resolvedEntity && resolvedEntity.timestamp) {
        // No CREATE action - fall back to using document timestamp
        events.push({
          timestamp: resolvedEntity.timestamp,
          description: 'MedPrompt received document via NHS MESH',
          isSystem: true
        });
      }
      
      // Add system event for readyForReviewAt
      if (auditData.readyToReviewAt) {
        events.push({
          timestamp: auditData.readyToReviewAt,
          description: 'MedPrompt finished processing',
          isSystem: true
        });
      }
      
      // Add user events with filtering
      if (auditData.userEvents) {
        auditData.userEvents.forEach(event => {
          const userAction = event.userAction;
          
          // Always show FILE_AWAY and DELETE (significant events)
          // For EDIT, OPEN, REGENERATE - check filter state
          const shouldShow = 
            userAction === 'FILE_AWAY' || 
            userAction === 'DELETE' || 
            actionFilters[userAction];
          
          if (shouldShow) {
            events.push({
              timestamp: event.userEvent.timestamp,
              description: formatEventDescription(event, activeUsers, EntityType.DOCUMENT, teams, resolvedEntity?.assigneeHistory),
              isSystem: false,
              userAction: event.userAction
            });
          }
        });
      }
    } else if (resolvedEntityType === EntityType.CONSULTATION) {
      // Add system event for consultation created/started (first chronologically)
      let createEvent = null;
      if (auditData.userEvents) {
        createEvent = auditData.userEvents.find(event => event.userAction === 'CREATE');
      }
      
      if (createEvent) {
        // CREATE action exists - determine who created the consultation
        const userId = createEvent.userEvent.userId;
        let description = 'Consultation created'; // default
        
        if (userId !== 'SYSTEM') {
          // User created the consultation
          const foundUser = activeUsers.find(u => u.userId === userId);
          const userName = foundUser ? foundUser.name : userId.substring(0, 5);
          description = `${userName} created consultation`;
        }
        
        events.push({
          timestamp: createEvent.userEvent.timestamp,
          description: description,
          isSystem: true
        });
      } else if (resolvedEntity && resolvedEntity.timestamp) {
        // No CREATE action - fall back to using consultation timestamp
        events.push({
          timestamp: resolvedEntity.timestamp,
          description: 'Consultation created',
          isSystem: true
        });
      }
      
      // Add system event for readyToReviewAt
      if (auditData.readyToReviewAt) {
        events.push({
          timestamp: auditData.readyToReviewAt,
          description: 'MedPrompt finished processing',
          isSystem: true
        });
      }
      
      // Add user events with filtering
      if (auditData.userEvents) {
        auditData.userEvents.forEach(event => {
          const userAction = event.userAction;
          
          // Always show COMPLETE_RECORDING and DELETE (significant events)
          // For EDIT, OPEN, REGENERATE - check filter state
          const shouldShow = 
            userAction === 'COMPLETE_RECORDING' || 
            userAction === 'DELETE' || 
            actionFilters[userAction];
          
          if (shouldShow) {
            events.push({
              timestamp: event.userEvent.timestamp,
              description: formatEventDescription(event, activeUsers, EntityType.CONSULTATION, teams),
              isSystem: false,
              userAction: event.userAction
            });
          }
        });
      }
    } else if (resolvedEntityType === EntityType.TASK) {
      // Add system event for task creation with attribution
      let creatorName = null;
      let creationTimestamp = resolvedEntity?.timestamp;
      
      // Try to find FILE_AWAY event in document audit to get creator and fallback timestamp
      if (documentAuditData && documentAuditData.userEvents) {
        const fileAwayEvent = documentAuditData.userEvents.find(
          event => event.userAction === 'FILE_AWAY'
        );
        
        if (fileAwayEvent) {
          // Get creator name from FILE_AWAY event
          if (fileAwayEvent.userEvent.userId) {
            const foundUser = activeUsers.find(u => u.userId === fileAwayEvent.userEvent.userId);
            creatorName = foundUser ? foundUser.name : fileAwayEvent.userEvent.userId.substring(0, 5);
          }
          
          // Fall back to FILE_AWAY timestamp if task.timestamp is not available
          if (!creationTimestamp && fileAwayEvent.userEvent.timestamp) {
            creationTimestamp = fileAwayEvent.userEvent.timestamp;
          }
        }
      }
      
      // Only add creation event if we have a timestamp
      if (creationTimestamp) {
        const description = creatorName 
          ? `Task created by ${creatorName}`
          : 'Task created';
        
        events.push({
          timestamp: creationTimestamp,
          description: description,
          isSystem: true
        });
      }
      
      // Add user events from task audit (auditData is a List<UserAuditEvent> for tasks)
      if (Array.isArray(auditData)) {
        auditData.forEach(event => {
          const userAction = event.userAction;
          
          // Always show COMPLETE and DELETE (significant events)
          // For EDIT, OPEN - check filter state
          const shouldShow = 
            userAction === 'COMPLETE' || 
            userAction === 'DELETE' || 
            actionFilters[userAction];
          
          if (shouldShow) {
            events.push({
              timestamp: event.userEvent.timestamp,
              description: formatEventDescription(event, activeUsers, EntityType.TASK, teams, resolvedEntity?.assigneeHistory),
              isSystem: false,
              userAction: event.userAction
            });
          }
        });
      }
    } else if (resolvedEntityType === EntityType.REFERRAL) {
      // Fallback creation marker only when audit data has no CREATE entry yet
      const hasCreateInAudit = Array.isArray(auditData) && auditData.some(e => e.userAction === 'CREATE');
      if (!hasCreateInAudit && resolvedEntity?.timestamp) {
        events.push({
          timestamp: resolvedEntity.timestamp,
          description: 'Referral created',
          isSystem: true
        });
      }

      // All events driven from auditData — mirrors referralActions() = [CREATE, OPEN, EDIT, COMPLETE, DELETE]
      if (Array.isArray(auditData)) {
        auditData.forEach(event => {
          const userAction = event.userAction;

          // CREATE, COMPLETE, DELETE are one-time events — always show regardless of filter
          // OPEN, EDIT are repeatable — respect the filter toggle
          const shouldShow =
            userAction === 'CREATE' ||
            userAction === 'COMPLETE' ||
            userAction === 'DELETE' ||
            actionFilters[userAction];

          if (!shouldShow) return;

          if (userAction === 'CREATE') {
            const userId = event.userEvent.userId;
            let creatorName = 'Unknown';
            if (userId === 'SYSTEM') {
              creatorName = 'MedPrompt App';
            } else if (userId) {
              const foundUser = activeUsers ? activeUsers.find(u => u.userId === userId) : null;
              creatorName = foundUser ? foundUser.name : userId.substring(0, 5);
            }
            events.push({
              timestamp: event.userEvent.timestamp,
              description: `Referral created by ${creatorName}`,
              isSystem: true,
              userAction
            });
          } else {
            events.push({
              timestamp: event.userEvent.timestamp,
              description: formatEventDescription(event, activeUsers, EntityType.REFERRAL, teams),
              isSystem: false,
              userAction
            });
          }
        });
      }
    }
    
    // Sort events by timestamp (newest first)
    return events.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
  };

  const timelineEvents = getTimelineEvents();

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-container activity-modal modal-animated" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Audit Trail</h2>
          <button className="close-button" onClick={onClose}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>
        
        {/* Fixed Filter Header */}
        <div className="activity-filter-header">
          <span className="filter-label">Show actions:</span>
          <div className="filter-checkboxes">
            {Object.entries(actionFilters).map(([action, checked]) => (
              <label key={action} className="filter-checkbox">
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => handleFilterToggle(action)}
                />
                <span className="checkmark"></span>
                <span className="action-label">{UserActionVerbs[action] || action.toLowerCase()}</span>
              </label>
            ))}
          </div>
        </div>
        
        <div className="modal-content activity-modal-content">
          {loading ? (
            <div className="loading-container">
              <FontAwesomeIcon icon={faSpinner} className="fa-spin" />
              <span>Loading activity...</span>
            </div>
          ) : error ? (
            <div className="error-container">
              <p>Failed to load document activity: {error.message || error}</p>
            </div>
          ) : (
            <div className="timeline-container">
              {timelineEvents.length === 0 ? (
                <div className="empty-timeline">
                  <p>No activity recorded for this document.</p>
                </div>
              ) : (
                <div className="timeline">
                  {timelineEvents.map((event, index) => (
                    <React.Fragment key={index}>
                      <div className={`timeline-item ${event.isSystem ? 'system-event' : 'user-event'}${event.userAction === 'EDIT' ? ' edit-event' : ''}${event.userAction === 'REGENERATE' ? ' regenerate-event' : ''}${event.userAction === 'COMPLETE_RECORDING' ? ' complete-recording-event' : ''}`}>
                        <div className="timeline-marker"></div>
                        <div className="timeline-content">
                          <div className="timeline-description">
                            {event.description}
                          </div>
                          <div className="timeline-timestamp">
                            {formatTimestamp(event.timestamp)}
                          </div>
                        </div>
                      </div>
                      {index < timelineEvents.length - 1 && (
                        <div className="timeline-time-diff">
                          <div className="time-diff-content">
                            {formatTimeDifference(event.timestamp, timelineEvents[index + 1].timestamp)}
                          </div>
                        </div>
                      )}
                    </React.Fragment>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export { EntityType };
export default React.memo(ActivityModal); 